import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { User, Plus, X, MessageCircle, Filter, Camera, Mic, Video, FileText, Check, CheckCheck } from 'lucide-react';
import { formatarHorarioContato } from '../utils/dateFormatter';
import { tagsAPI } from '../services/api';
import { formatWhatsAppPreview } from './WhatsAppMessage';
import { SkeletonLinhaContato } from './Skeleton';

const StatusIcon = ({ status }) => {
  const s = status?.toLowerCase();
  if (!s) return null;
  if (s === 'read') return <CheckCheck size={14} className="text-blue-400 flex-shrink-0" />;
  if (s === 'delivered' || s === 'received') return <CheckCheck size={14} className="opacity-50 flex-shrink-0" />;
  if (s === 'failed' || s === 'error') return <span className="text-red-400 flex-shrink-0">!</span>;
  if (s === 'sent') return <Check size={14} className="opacity-50 flex-shrink-0" />;
  return null;
};

const formatPreview = (text) => {
  if (!text) return '';
  // Mídia padrão
  const m = text.match(/^\[(Imagem|Áudio|Vídeo|Documento)\]\s*(.*)?$/);
  if (m) {
    const icons = { Imagem: <Camera size={14} />, Áudio: <Mic size={14} />, Vídeo: <Video size={14} />, Documento: <FileText size={14} /> };
    const labels = { Imagem: 'Foto', Áudio: 'Áudio', Vídeo: 'Vídeo', Documento: m[2] || 'Documento' };
    return <span className="inline-flex items-center gap-1 opacity-70">{icons[m[1]]}{labels[m[1]]}</span>;
  }
  // Posts/Reels/Stories compartilhados
  const s = text.match(/^\[(Post|Reel|Story)\]\s*(.*)?/s);
  if (s) {
    const labels = { Post: '📷 Post', Reel: '🎬 Reel', Story: '📸 Story' };
    return <span className="inline-flex items-center gap-1 opacity-70">{labels[s[1]]}</span>;
  }
  // Mensagens especiais
  if (text === '[Mídia temporária]') return <span className="opacity-70">⏱️ Mídia temporária</span>;
  if (text === '[Mensagem não suportada]') return <span className="opacity-70">⚠️ Não suportada</span>;
  return formatWhatsAppPreview(text, 80);
};

const ContactItem = memo(({ contato, isActive, onClick, darkMode, tags, mostrarInstancia, currentUserId, digitandoNome }) => {
  // `tags` chega como Map pré-computado: antes era tags.filter() por item, ou
  // seja O(conversas x tags) a cada render da lista.
  const contatoTags = useMemo(
    () => (contato.tags || []).map((id) => tags.get(id)).filter(Boolean),
    [contato.tags, tags],
  );

  // Handler estável por item. Com arrow inline no JSX do pai, a prop mudava a
  // cada render e o memo deste componente nunca segurava nenhuma linha.
  const handleClick = useCallback(() => onClick(contato), [onClick, contato]);
  
  // Pegar iniciais do nome
  const getInitials = (nome) => {
    if (!nome) return '?';
    const parts = nome.split(' ').filter(p => p.length > 0);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const isMinhaConversa = contato.atendente_id === currentUserId;
  const temAtendente = !!contato.atendente_id;
  
  return (
    <div
      className={`p-3 cursor-pointer border-b transition-colors ${
        isActive 
          ? darkMode 
            ? 'bg-zinc-700 border-l-4 border-l-green-500' 
            : 'bg-green-50 border-l-4 border-l-green-500' 
          : darkMode
            ? 'border-gray-700 hover:bg-zinc-700'
            : 'border-gray-100 hover:bg-gray-50'
      }`}
      onClick={handleClick}
    >
      <div className="flex items-center space-x-3">
        <div className="relative">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold overflow-hidden ${
            darkMode ? 'bg-zinc-600 text-gray-300' : 'bg-gray-300 text-gray-700'
          }`}>
            {contato.foto_contato ? (
              <img src={contato.foto_contato} alt="" className="w-full h-full object-cover" />
            ) : (
              getInitials(contato.nome_contato)
            )}
          </div>
          {/* Badge do atendente */}
          {temAtendente && (
            <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
              isMinhaConversa
                ? 'bg-nomoz-orange text-white border-white dark:border-zinc-800'
                : 'bg-gray-500 text-white border-white dark:border-zinc-800'
            }`} title={contato.atendente_nome || 'Atendente'}>
              {(contato.atendente_nome || '?')[0].toUpperCase()}
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className={`font-medium truncate ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {contato.nome_contato}
            </h3>
            <span className={`text-xs ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {formatarHorarioContato(contato.create_at)}
            </span>
          </div>
          <div className="mt-1">
            <p className={`text-sm truncate ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {digitandoNome ? (
                <span className="text-blue-500 italic">{digitandoNome} digitando...</span>
              ) : contato.eh_resposta ? (
                <span className="inline-flex items-center gap-1 truncate">
                  <StatusIcon status={contato.status_mensagem} />
                  {formatPreview(contato.ultima_mensagem)}
                </span>
              ) : (
                formatPreview(contato.ultima_mensagem)
              )}
            </p>
          </div>
          {contatoTags.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {contatoTags.map(tag => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  style={{ backgroundColor: tag.cor + '20', color: tag.cor }}
                >
                  {tag.nome}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const ContactList = ({ 
  contatos, 
  carregando = false,
  contatoAtivo, 
  onSelectContact, 
  conectado, 
  onRefresh, 
  onNewConversation,
  darkMode,
  instanciaFiltro,
  currentUserId,
  atendentesDigitando = {},
}) => {
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState('tudo'); // 'tudo', 'nao_lidas'
  const [showFiltroDropdown, setShowFiltroDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (user.cliente_id) {
      tagsAPI.list(user.cliente_id).then(res => setTags(res.data || [])).catch(console.error);
    }
  }, [user.cliente_id]);

  // Três filtros em cadeia, antes recalculados no corpo do componente a cada
  // render — inclusive ao digitar em qualquer campo da tela.
  const contatosFiltrados = useMemo(() => {
    let lista = contatos;

    if (searchTerm.trim()) {
      const termo = searchTerm.toLowerCase();
      lista = lista.filter(
        (c) =>
          c.nome_contato?.toLowerCase().includes(termo) ||
          c.telefone?.includes(searchTerm),
      );
    }

    if (selectedTags.length > 0) {
      lista = lista.filter((c) =>
        selectedTags.some((tagId) => c.tags?.includes(tagId)),
      );
    }

    if (filtroAtivo === 'nao_lidas') {
      lista = lista.filter((c) => c.tem_pendente);
    }

    return lista;
  }, [contatos, searchTerm, selectedTags, filtroAtivo]);

  // Índice de tags por id, montado uma vez por mudança de `tags` em vez de um
  // filter por linha da lista.
  const tagsPorId = useMemo(
    () => new Map((tags || []).map((t) => [t.id, t])),
    [tags],
  );

  const tagsFiltradas = useMemo(
    () =>
      tags.filter((t) =>
        t.nome.toLowerCase().includes(tagSearch.toLowerCase()),
      ),
    [tags, tagSearch],
  );
  
  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
    setShowFiltroDropdown(false);
  };

  return (
    <div className={`w-full md:w-96 h-full md:border-r flex flex-col md:flex-shrink-0 ${
      darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className={`p-3 border-b ${
        darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <h1 className={`text-xl font-semibold ${
            darkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Conversas
          </h1>
          
          {/* Botão Nova Conversa - Balão com + */}
          {onNewConversation && <button
            onClick={onNewConversation}
            className={`w-10 h-10 rounded-full flex items-center justify-center relative transition-colors ${
              darkMode
                ? 'bg-zinc-700 hover:bg-zinc-600 text-gray-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
            title="Nova conversa"
          >
            <MessageCircle className="w-5 h-5" />
            <div className="absolute top-0 right-0 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
              <Plus className="w-3 h-3 text-white" />
            </div>
          </button>}
        </div>
        
        {/* Barra de pesquisa de conversas */}
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Pesquisar ou começar uma nova conversa"
          className={`w-full px-4 py-2 rounded-lg text-sm mb-3 ${
            darkMode
              ? 'bg-zinc-700 text-white placeholder-gray-400'
              : 'bg-gray-100 text-gray-900 placeholder-gray-500'
          } focus:outline-none`}
        />
        
        {/* Abas de filtro */}
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => { setFiltroAtivo('tudo'); setSelectedTags([]); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filtroAtivo === 'tudo' && selectedTags.length === 0
                ? darkMode ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
                : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tudo
          </button>
          
          <button
            onClick={() => { setFiltroAtivo('nao_lidas'); setSelectedTags([]); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filtroAtivo === 'nao_lidas'
                ? darkMode ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
                : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Não lidas
          </button>
          
          {/* Botão de filtro por tags */}
          <div className="relative flex-1">
            <button
              onClick={() => setShowFiltroDropdown(!showFiltroDropdown)}
              className={`w-full px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                selectedTags.length > 0
                  ? darkMode ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Filter className="w-3 h-3" />
              Tags
            </button>
            
            {/* Dropdown de tags */}
            {showFiltroDropdown && (
              <div className={`absolute z-20 w-64 right-0 mt-1 rounded-lg shadow-lg border ${
                darkMode ? 'bg-zinc-700 border-zinc-600' : 'bg-white border-gray-200'
              }`}>
                <div className="p-2">
                  <input
                    type="text"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Buscar tags..."
                    className={`w-full text-xs px-2 py-1.5 rounded border mb-2 ${
                      darkMode
                        ? 'bg-zinc-600 border-zinc-500 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="max-h-48 overflow-y-auto">
                    {tagsFiltradas.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 rounded ${
                          darkMode ? 'hover:bg-zinc-600' : 'hover:bg-gray-100'
                        }`}
                      >
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.cor }}
                        />
                        <span className="flex-1">{tag.nome}</span>
                        {selectedTags.includes(tag.id) && <span className="text-green-500">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Tags selecionadas */}
        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedTags.map(tagId => {
              const tag = tags.find(t => t.id === tagId);
              return tag ? (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white cursor-pointer hover:opacity-80"
                  style={{ backgroundColor: tag.cor }}
                  onClick={() => toggleTag(tag.id)}
                >
                  {tag.nome}
                  <X className="h-3 w-3" />
                </span>
              ) : null;
            })}
          </div>
        )}
        
        <div className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {carregando ? 'Carregando…' : `${contatosFiltrados.length} conversas`}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 pb-20">
        {/* Antes desta guarda, durante a carga a lista ficava vazia e o contador
            marcava "0 conversas" — indistinguível de uma conta sem conversas. */}
        {carregando && contatosFiltrados.length === 0 && (
          <div role="status" aria-busy="true" aria-label="Carregando conversas">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonLinhaContato key={i} />
            ))}
          </div>
        )}

        {!carregando && contatosFiltrados.length === 0 && (
          <div
            className={`px-4 py-10 text-center text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            {/* `contatos` já vem filtrado pelo pai (filtro de atendente), então
                não dá para afirmar daqui que a conta não tem conversas. */}
            Nenhuma conversa encontrada com esses filtros.
          </div>
        )}

        {contatosFiltrados.map((contato) => (
          <ContactItem
            key={`${contato.telefone}_${contato.instancia_id || 'sem'}`}
            contato={contato}
            isActive={contatoAtivo?.telefone === contato.telefone && contatoAtivo?.instancia_id === contato.instancia_id}
            onClick={onSelectContact}
            darkMode={darkMode}
            tags={tagsPorId}
            mostrarInstancia={!instanciaFiltro}
            currentUserId={currentUserId}
            digitandoNome={atendentesDigitando[contato.telefone]}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(ContactList);